import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { CssBaseline, ThemeProvider, useMediaQuery } from "@mui/material";
import {
  ACCENTS,
  AccentKey,
  ThemeMode,
  applyCssVariables,
  makeTheme,
} from "./theme";

type ThemeChoice = {
  mode: ThemeMode;
  accent: AccentKey;
  setMode: (m: ThemeMode) => void;
  setAccent: (a: AccentKey) => void;
};

const ThemeChoiceContext = createContext<ThemeChoice>({
  mode: "system",
  accent: "forest",
  setMode: () => {},
  setAccent: () => {},
});

export const useThemeChoice = () => useContext(ThemeChoiceContext);

// Remembered on this device only: each person can pick their own look.
function load<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const v = localStorage.getItem(key) as T | null;
    return v && allowed.includes(v) ? v : fallback;
  } catch {
    return fallback;
  }
}

function save(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // private browsing: the choice lasts for this visit only
  }
}

export default function ThemeRoot({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() =>
    load("themeMode", ["light", "dark", "system"] as const, "system")
  );
  const [accent, setAccentState] = useState<AccentKey>(() =>
    load("themeAccent", Object.keys(ACCENTS) as AccentKey[], "forest")
  );
  const prefersDark = useMediaQuery("(prefers-color-scheme: dark)");
  const resolved = mode === "system" ? (prefersDark ? "dark" : "light") : mode;

  const theme = useMemo(() => makeTheme(resolved, accent), [resolved, accent]);
  useEffect(() => applyCssVariables(resolved, accent), [resolved, accent]);

  const value = useMemo(
    () => ({
      mode,
      accent,
      setMode: (m: ThemeMode) => {
        setModeState(m);
        save("themeMode", m);
      },
      setAccent: (a: AccentKey) => {
        setAccentState(a);
        save("themeAccent", a);
      },
    }),
    [mode, accent]
  );

  return (
    <ThemeChoiceContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeChoiceContext.Provider>
  );
}
